<template>
  <slide-transition
    v-if="dsComponentDetails.version === VersionOptions.LEGACY"
    :direction="side"
    :position="{ start: 'fixed', end: 'fixed' }"
  >
    <div
      v-if="active"
      v-click-outside="handleClickOutside"
      data-sb-regression="sb-drawer"
      class="sb-drawer"
      :class="[
        `sb-drawer--${side}`,
        $slots['drawer-footer'] ? 'sb-drawer--w-footer' : '',
        containerClass,
      ]"
    >
      <span
        class="sb-drawer__quit"
        :class="[
          `sb-drawer__quit--${closeVariation}`
        ]"
      >
        <back-icon
          v-if="closeVariation == 'back'"
          class="sb-drawer__quit-icon"
          :class="[`sb-drawer__quit-icon--${side}`]"
          @click.prevent="handleClose"
        />
        <button
          v-else-if="closeVariation == 'x'"
          class=""
          @click.prevent="handleClose"
        >
          <img
            :src="XIcon"
            :alt="$t('sbDrawer.closePanelAltText')"
            class="x-icon"
          >
        </button>
      </span>
      <div
        v-if="$slots['drawer-header']"
        class="sb-drawer__header"
      >
        <slot
          name="drawer-header"
          :close="handleClose"
        />
      </div>
      <div
        class="sb-drawer__well"
        :class="[removePadding ? 'remove-padding' : '']"
      >
        <!--
          @slot slot to show drawer contents
        -->
        <slot />
      </div>
      <div
        v-if="$slots['drawer-footer']"
        class="sb-drawer__footer"
      >
        <slot
          name="drawer-footer"
          :close="handleClose"
        />
      </div>
    </div>
  </slide-transition>
  <SbDrawerNext
    v-else
    :side="side"
    :active="active"
    :init-overrides="initOverrides"
    :destroy-overrides="destroyOverrides"
    :close-variation="closeVariation"
    :close-on-outside-click="closeOnOutsideClick"
    :container-class="containerClass"
    :remove-padding="removePadding"
    @update:active="$emit('update:active', $event)"
  >
    <template
      v-if="$slots['drawer-header']"
      #drawer-header
    >
      <slot
        name="drawer-header"
        :close="handleClose"
      />
    </template>
    <template
      v-if="$slots['drawer-footer']"
      #drawer-footer
    >
      <slot
        name="drawer-footer"
        :close="handleClose"
      />
    </template>
    <template
      v-if="$slots['drawer-container']"
      #drawer-container
    >
      <slot name="drawer-container" />
    </template>
    <template #default>
      <slot />
    </template>
  </SbDrawerNext>
</template>

<script>
/**
 * A Drawer that slides out from the right or left of the screen.
 * It may accept any content in the slot, and is vertically scrollable.
 */

import ClickOutsideDirectiveMixin from '@src/utils/directives/clickOutsideDirective';
import EscapeKeyMixin from '@src/utils/mixins/escapeKey';
import SlideTransition from '@src/components/Transitions/Slide.vue';
import BackIcon from '@src/components/Icons/components/BackIcon.vue';
import XIcon from '@public/media/icons/x.svg';
import { hideChat, unHideChat } from '@src/utils/chatHelper';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';
import SbDrawerNext from '@src/components/Drawer/SbDrawerNext.vue';
import drawerProps from '@src/components/Drawer/props';

export default {
  name: 'SbDrawer',
  components: { SlideTransition, BackIcon, SbDrawerNext },
  mixins: [EscapeKeyMixin, ClickOutsideDirectiveMixin, versionedComponentsMixin],
  props: {
    ...drawerProps,
  },
  emits: [
    /**
    * Emitted when the drawer is closed
    */
    'update:active',
  ],
  data: () => ({
    XIcon,
    closeGuard: true,
  }),
  computed: {},
  watch: {
    active: {
      handler(newVal) {
        if (this.dsComponentDetails.version === this.VersionOptions.NEXT) return;
        this.$nextTick(() => { this.setGuard(!newVal); });
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.dsComponentDetails.version === this.VersionOptions.NEXT) return;
    if (this.initOverrides) this.initOverrides(document);
    else this.hideHelpChat();
  },
  unmounted() {
    if (this.dsComponentDetails.version === this.VersionOptions.NEXT) return;
    if (this.destroyOverrides) this.destroyOverrides(document);
    else this.showHelpChat(document);
  },
  methods: {
    setGuard(val) {
      // Use this for the click outside directive
      // If we don't set this, then triggering the drawer from an outside
      // element closes the drawer immediately after opening
      setTimeout(() => {
        this.closeGuard = val;
      }, 0);
    },
    handleClose() {
      if (this.closeGuard) return;
      this.$emit('update:active', false);
    },
    handleClickOutside() {
      if (this.closeOnOutsideClick) {
        this.handleClose();
      }
    },
    escapeKeyHandler(e) {
      this.handleClose(e);
    },
    bypassHideAndShow() {
      return this.side === 'left';
    },
    hideHelpChat() {
      if (this.bypassHideAndShow()) return;
      /* Hide help chat button on drawer open */
      hideChat();
    },
    showHelpChat(document) {
      if (this.bypassHideAndShow()) return;
      /* Show help chat button on drawer open */
      const openDrawers = document?.getElementsByClassName('sb-drawer');
      if (openDrawers?.length) return; // leave hidden if more drawers remain open
      unHideChat();
    },
  },
};

</script>
<style lang='scss'>
@import '@src/components/Drawer/style/sb-drawer.scss';
</style>
